import React, { useEffect, useState, useCallback, useRef } from "react";
import { useApp } from "../context/AppContext";
import api from "../api";
import { toast } from "sonner";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Plus, Trash2, FileUp, Download } from "lucide-react";

const EMPTY_FORM = { type: "client", name: "", ragione_sociale: "", vat_number: "", tax_code: "", email: "", street_type: "via", street: "", cap: "", city: "", province: "", country: "IT", recipient_code: "" };
const STREET_TYPES = ["Via", "Piazza", "Vicolo", "Calle", "Corso", "Largo", "Strada"];

export default function Contacts() {
  const { t, entity } = useApp();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [visuraFor, setVisuraFor] = useState(null);
  const [uploading, setUploading] = useState(false);
  const visuraRef = useRef(null);

  const load = useCallback(() => {
    api.get(`/contacts?entity_id=${entity}`).then((r) => setItems(r.data)).catch(() => {});
  }, [entity]);

  useEffect(() => { load(); }, [load]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    if (!form.name.trim()) {
      toast.error(t("contacts.fullName"));
      return;
    }
    try {
      await api.post("/contacts", { ...form, entity_id: entity });
      toast.success(t("msg.saved"));
      setOpen(false);
      setForm(EMPTY_FORM);
      load();
    } catch {
      toast.error(t("msg.error"));
    }
  };

  const uploadVisura = async (file) => {
    if (!file || !visuraFor) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/contacts/${visuraFor}/visura`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(t("msg.saved"));
      load();
    } catch {
      toast.error(t("msg.error"));
    } finally {
      setUploading(false);
      setVisuraFor(null);
      if (visuraRef.current) visuraRef.current.value = "";
    }
  };

  const downloadVisura = async (c) => {
    try {
      const res = await api.get(`/files/download?path=${encodeURIComponent(c.visura_path)}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = c.visura_filename || "visura.pdf";
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error(t("msg.error"));
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/contacts/${id}`);
      toast.success(t("msg.deleted"));
      load();
    } catch {
      toast.error(t("msg.error"));
    }
  };

  return (
    <div className="space-y-6" data-testid="contacts-page">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">{t("contacts.title")}</h1>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="new-contact-button"><Plus className="h-4 w-4 mr-2" />{t("common.add")}</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>{t("contacts.title")}</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>{t("common.type")}</Label>
                <Select value={form.type} onValueChange={(v) => set("type", v)}>
                  <SelectTrigger data-testid="contact-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="client">{t("contacts.client")}</SelectItem>
                    <SelectItem value="supplier">{t("contacts.supplier")}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div><Label>{t("contacts.fullName")}</Label>
                  <Input value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="contact-name-input" /></div>
                <div><Label>{t("contacts.ragione")}</Label>
                  <Input value={form.ragione_sociale} onChange={(e) => set("ragione_sociale", e.target.value)} data-testid="contact-ragione-input" /></div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div><Label>P.IVA / VAT</Label>
                  <Input value={form.vat_number} onChange={(e) => set("vat_number", e.target.value)} data-testid="contact-vat-input" /></div>
                <div><Label>C.F. / Tax code</Label>
                  <Input value={form.tax_code} onChange={(e) => set("tax_code", e.target.value)} data-testid="contact-taxcode-input" /></div>
              </div>
              <div><Label>Email</Label>
                <Input value={form.email} onChange={(e) => set("email", e.target.value)} data-testid="contact-email-input" /></div>
              <div><Label>{t("contacts.recipientCode")}</Label>
                <Input value={form.recipient_code} onChange={(e) => set("recipient_code", e.target.value)} maxLength={7} placeholder="0000000" data-testid="contact-recipientcode-input" /></div>
              <div className="grid grid-cols-3 gap-4">
                <div><Label>{t("contacts.streetType")}</Label>
                  <Select value={form.street_type} onValueChange={(v) => set("street_type", v)}>
                    <SelectTrigger data-testid="contact-streettype-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {STREET_TYPES.map((s) => <SelectItem key={s} value={s.toLowerCase()}>{s}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="col-span-2"><Label>{t("contacts.street")}</Label>
                  <Input value={form.street} onChange={(e) => set("street", e.target.value)} placeholder="es. Roma, 10" data-testid="contact-street-input" /></div>
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div><Label>{t("contacts.cap")}</Label>
                  <Input value={form.cap} onChange={(e) => set("cap", e.target.value)} data-testid="contact-cap-input" /></div>
                <div><Label>{t("contacts.city")}</Label>
                  <Input value={form.city} onChange={(e) => set("city", e.target.value)} data-testid="contact-city-input" /></div>
                <div><Label>{t("contacts.province")}</Label>
                  <Input value={form.province} onChange={(e) => set("province", e.target.value)} maxLength={2} placeholder="MI" data-testid="contact-province-input" /></div>
              </div>
              <Button className="w-full" onClick={submit} data-testid="contact-submit-button">{t("common.save")}</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
      <input ref={visuraRef} type="file" accept="application/pdf" className="hidden"
        data-testid="visura-file-input" onChange={(e) => uploadVisura(e.target.files[0])} />
      <Card>
        <CardContent className="p-0">
          {items.length === 0 ? <div className="p-8 text-center text-sm text-muted-foreground">{t("common.noData")}</div> : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t("contacts.fullName")}</TableHead>
                  <TableHead>{t("contacts.ragione")}</TableHead>
                  <TableHead>{t("common.type")}</TableHead>
                  <TableHead>{t("contacts.city")}</TableHead>
                  <TableHead>P.IVA / VAT</TableHead>
                  <TableHead>{t("contacts.visura")}</TableHead>
                  <TableHead>{t("common.actions")}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((c) => (
                  <TableRow key={c.id} data-testid={`contact-row-${c.id}`}>
                    <TableCell className="font-medium">{c.name}</TableCell>
                    <TableCell>{c.ragione_sociale || "—"}</TableCell>
                    <TableCell>
                      <Badge variant={c.type === "client" ? "default" : "secondary"}>
                        {c.type === "client" ? t("contacts.client") : t("contacts.supplier")}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-sm">
                      {c.city ? `${c.city}${c.province ? ` (${c.province})` : ""}` : "—"}
                    </TableCell>
                    <TableCell className="font-mono text-xs">{c.vat_number || "—"}</TableCell>
                    <TableCell>
                      {c.visura_path ? (
                        <div className="flex items-center gap-1">
                          <Badge variant="outline" className="text-[10px] max-w-[120px] truncate">{c.visura_filename}</Badge>
                          <Button size="icon" variant="ghost" data-testid={`download-visura-${c.id}`} onClick={() => downloadVisura(c)}>
                            <Download className="h-4 w-4" />
                          </Button>
                        </div>
                      ) : (
                        <Button size="sm" variant="ghost" data-testid={`upload-visura-${c.id}`} disabled={uploading}
                          onClick={() => { setVisuraFor(c.id); setTimeout(() => visuraRef.current?.click(), 0); }}>
                          <FileUp className="h-4 w-4 mr-1" />PDF
                        </Button>
                      )}
                    </TableCell>
                    <TableCell>
                      <Button size="icon" variant="ghost" data-testid={`delete-contact-${c.id}`} onClick={() => remove(c.id)}>
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
